/**
 * PAYSTUB WIZARD - MAIN COMPONENT (ENHANCED v4)
 * 
 * FEATURES:
 * 1. CLIENT SELECTION: Choose previous clients or create new on entry
 * 2. AUTO-SAVE: All data persists in DATABASE - survives deployment
 * 3. AUTO DATE CALCULATION: Change pay date → auto-calculate period dates
 * 4. Save clients after download for future editing
 */

/* eslint-disable */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import PinProtection from './PinProtection';
import TemplateGallery, { PAYSTUB_TEMPLATES } from './TemplateGallery';
import CompanyInfoStep from './steps/CompanyInfoStep';
import EmployeeInfoStep from './steps/EmployeeInfoStep';
import EmploymentDetailsStep from './steps/EmploymentDetailsStep';
import PaystubDetailsStep from './steps/PaystubDetailsStep';
import PreviewStep from './steps/PreviewStep';
import UploadPaystubStep from './steps/UploadPaystubStep';
import W2CreatorModal from './W2CreatorModal';
import W2Studio from './w2studio/W2Studio';
import PaystubRedatePanel from './paystub-update/PaystubRedatePanel';
import { PaydayHub } from './PaydayHub';
import { commitCheckNumbers } from '../lib/checkNumbers';
import ClientStubHistory from './paystub-update/ClientStubHistory';
import ClientOverview from './paystub-update/ClientOverview';
import { ClientRowActions, ClientButtonKey } from './paystub-update/ClientRowActions';
import NEC1099CreatorModal from './NEC1099CreatorModal';
import {
  Building2,
  User,
  Briefcase,
  Calculator,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  Home,
  Download,
  Palette,
  CheckCircle2,
  Save,
  FolderOpen,
  Trash2,
  Star,
  Plus,
  Users,
  Edit,
  Clock,
  Upload,
  Loader2,
  Database,
  History,
  RotateCcw,
  AlertTriangle,
  Search,
  Copy,
  Filter,
  TrendingUp,
  DollarSign,
  Calendar,
  ArrowUpDown,
  RefreshCw,
  CalendarPlus,
  QrCode,
  FileText,
  X
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const API = process.env.REACT_APP_BACKEND_URL;

// Step 0 is template selection (shown first after PIN)
const STEPS = [
  { id: 0, title: 'Choose Template', icon: Palette },
  { id: 1, title: 'Company Info', icon: Building2 },
  { id: 2, title: 'Employee Info', icon: User },
  { id: 3, title: 'Employment Details', icon: Briefcase },
  { id: 4, title: 'Paystub Details', icon: Calculator },
  { id: 5, title: 'Preview & Download', icon: FileCheck }
];

const INITIAL_FORM_DATA = {
  // Template (selected first)
  selectedTemplate: null,
  colorScheme: 'darkgrey', // 'darkgrey' or 'black'
  
  // Company Info
  companyName: '',
  companyPhone: '',
  companyName2: '',  // DBA or second line for long company names
  companyAddress1: '',
  companyAddress2: '',
  companyCity: '',
  companyState: 'CA',
  companyZip: '',
  companyLogo: null,
  companyLogoPreview: null,
  companyLogoType: null,
  companyLogoBase64: null,
  
  // Employee Info
  employeeName: '',
  employeePhone: '',
  employeeSSN: '',
  employeeId: '',
  exemptions: 1,
  directDeposit: true,
  filingStatus: 'single',
  bankName: '',
  accountNumber: '',
  employeeAddress1: '',
  employeeAddress2: '',
  employeeCity: '',
  employeeState: 'CA',
  employeeZip: '',
  
  // Employment Details
  employmentType: 'employee',
  paymentType: 'hourly',
  payFrequency: 'bi-weekly',
  taxState: 'CA',
  numberOfStubs: 1,
  
  // Pay Details (for hourly)
  hourlyRate: 20,
  hoursPerPeriod: 80,
  overtimeHours: 0,
  
  // Pay Details (for salary)
  annualSalary: 52000,
  
  // Pay Period
  periodStartDate: new Date().toISOString().split('T')[0],
  
  // Tax Intensity (1-10)
  taxIntensity: 3,
  
  // Target Net Pay Mode (REVERSE CALCULATION)
  targetNetPayMode: false,
  targetNetPay: '',
  
  // Year Selection
  selectedYear: new Date().getFullYear(),
  
  // Generated Stubs
  paystubs: []
};

// Local Storage keys
const SAVED_CLIENTS_KEY = 'paystub_saved_clients';
const CURRENT_SESSION_KEY = 'paystub_current_session';
const WIZARD_STATE_KEY = 'paystub_wizard_state';

export default function PaystubWizard({ onExit }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedClients, setSavedClients] = useState([]);
  const [showClientSelection, setShowClientSelection] = useState(true);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [showUploadScreen, setShowUploadScreen] = useState(false);
  const [uploadedPaystubData, setUploadedPaystubData] = useState(null);
  const [uploadUpdateMode, setUploadUpdateMode] = useState(false);
  const [redateSource, setRedateSource] = useState(null);
  const [showW2Studio, setShowW2Studio] = useState(false);
  const [stubHistoryClient, setStubHistoryClient] = useState(null);
  const [showButtonKey, setShowButtonKey] = useState(false);
  const [overviewClient, setOverviewClient] = useState(null);
  const pendingClientIdRef = useRef(null);
  const lastAutoSavedRef = useRef(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  // 2026-06-12: W-2 popup state — opens after a paystub is downloaded
  const [w2PromptOpen, setW2PromptOpen] = useState(false);
  const [w2ModalOpen, setW2ModalOpen] = useState(false);
  // 2026-02-08 user request — allow up to 4 W-2s from a single paystub.
  // `w2Years` is the ORDERED queue of years the user still needs to
  // generate.  The wizard opens the W2CreatorModal for the FIRST year;
  // when that year's PDF downloads (or the modal closes), we pop the
  // year and re-open the modal for the next until the queue is empty.
  const [w2Years, setW2Years] = useState([]);          // e.g. [2025, 2024]
  const [w2YearIndex, setW2YearIndex] = useState(0);    // which one is open now
  const [w2Count, setW2Count] = useState(1);           // 1-4 in the prompt
  const [w2SelectedYears, setW2SelectedYears] = useState(() => [new Date().getFullYear() - 1]);
  // 2026-06-22: 1099-NEC modal — sibling option to W-2 in the post-paystub prompt
  const [necModalOpen, setNecModalOpen] = useState(false);
  // 2026-06-24: Year picked inline on the tax-form prompt.  Defaults to the
  // prior tax year (most common W-2 / 1099 filing scenario) but the user
  // can pick the current year or any of the prior 6 right from the prompt.
  const [taxFormYear, setTaxFormYear] = useState(() => new Date().getFullYear() - 1);
  // 2026-02-08 — Saved W-2s per current client. When populated, we can
  // one-tap-reopen the W-2 modal pre-filled from a prior year so the user
  // just tweaks the tax year and downloads a fresh PDF.
  const [savedW2s, setSavedW2s] = useState([]);              // for the current client only
  const [savedW2sByClient, setSavedW2sByClient] = useState({}); // client_id → count for the picker
  const [w2SeedFromSaved, setW2SeedFromSaved] = useState(null); // full saved doc to hydrate the modal
  const [showHistory, setShowHistory] = useState(false);
  const [trashedClients, setTrashedClients] = useState([]);
  const [generationHistory, setGenerationHistory] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('lastModified'); // 'name', 'lastModified', 'created'
  const [favoriteClients, setFavoriteClients] = useState(() => {
    const saved = localStorage.getItem('paystub_favorites');
    return saved ? JSON.parse(saved) : [];
  });

  // Load saved clients from DATABASE on mount
  const loadSavedClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const response = await axios.get(`${API}/api/paystub-clients`);
      if (response.data.success) {
        const clients = response.data.clients.map(c => ({
          id: c.id,
          name: `${c.company_name || 'Company'} - ${c.employee_name || 'Employee'}`,
          savedAt: c.created_at,
          lastModified: c.updated_at,
          formData: c.form_data || {},
        }));
        setSavedClients(clients);
      }
    } catch (e) {
      console.error('Error reloading clients:', e);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  // 2026-02-08 — helpers for saved W-2s (per-client history).
  const refreshSavedW2sForClient = useCallback(async (clientId) => {
    if (!clientId) { setSavedW2s([]); return; }
    try {
      const res = await axios.get(`${API}/api/w2-saved`, { params: { paystub_client_id: clientId } });
      if (res.data.success) setSavedW2s(res.data.w2s || []);
    } catch (e) {
      console.error('Error loading saved W-2s for client:', e);
    }
  }, []);

  const refreshAllSavedW2Counts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/w2-saved`);
      if (res.data.success) {
        const map = {};
        for (const w of (res.data.w2s || [])) {
          if (!w.paystub_client_id) continue;
          map[w.paystub_client_id] = (map[w.paystub_client_id] || 0) + 1;
        }
        setSavedW2sByClient(map);
      }
    } catch (e) {
      console.error('Error loading saved W-2 counts:', e);
    }
  }, []);

  useEffect(() => {
    // Check authentication
    const sessionAuth = sessionStorage.getItem('paystub_authenticated');
    if (sessionAuth === 'true') {
      setIsAuthenticated(true);
    }
    
    // Load saved clients from DATABASE (not localStorage)
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await axios.get(`${API}/api/paystub-clients`);
        if (response.data.success) {
          // Transform database format to wizard format
          const clients = response.data.clients.map(c => ({
            id: c.id,
            name: `${c.company_name || 'Company'} - ${c.employee_name || 'Employee'}`,
            savedAt: c.created_at,
            lastModified: c.updated_at,
            formData: c.form_data || {
              employeeName: c.employee_name,
              employeeSSN: c.employee_ssn,
              employeeAddress: c.employee_address,
              employeeCity: c.employee_city,
              employeeState: c.employee_state,
              employeeZip: c.employee_zip,
              companyName: c.company_name,
              companyAddress: c.company_address,
              companyCity: c.company_city,
              companyState: c.company_state,
              companyZip: c.company_zip,
              companyEIN: c.company_ein,
              payFrequency: c.pay_frequency,
              paymentType: c.payment_type,
              hourlyRate: c.hourly_rate,
              salary: c.salary,
              taxState: c.tax_state,
              filingStatus: c.filing_status,
              numberOfStubs: c.number_of_stubs,
              paystubs: c.paystubs,
              selectedTemplate: c.template_id,
              colorTheme: c.color_theme
            }
          }));
          setSavedClients(clients);
        }
      } catch (e) {
        console.error('Error loading saved clients from database:', e);
        // Fallback to localStorage if database fails
        const localClients = localStorage.getItem(SAVED_CLIENTS_KEY);
        if (localClients) {
          try {
            setSavedClients(JSON.parse(localClients));
          } catch (parseErr) {
            console.error('Error parsing local clients:', parseErr);
          }
        }
      } finally {
        setLoadingClients(false);
      }
    };
    
    fetchClients();
    
    // 2026-02-08 — kick off a global tally of saved W-2s per client so the
    // "N saved" badge on the picker rows renders on first paint.
    refreshAllSavedW2Counts();

    // Also fetch generation history for quick stats
    const fetchHistoryForStats = async () => {
      try {
        const response = await axios.get(`${API}/api/paystub-generation-history?days=30&limit=100`);
        if (response.data.success) {
          setGenerationHistory(response.data.logs || []);
        }
      } catch (e) {
        console.error('Error fetching history for stats:', e);
      }    };
    fetchHistoryForStats();
    
    // ALWAYS show client selection screen first - do NOT auto-restore session
    // Users can choose to continue with a saved client or create new
    setShowClientSelection(true);
  }, []);
  
  // 2026-02-08 — Keep the "saved W-2s for this client" list in sync when
  // the operator jumps between clients.
  useEffect(() => {
    refreshSavedW2sForClient(currentClientId);
  }, [currentClientId, refreshSavedW2sForClient]);
  
  // Handle browser back button - go to previous step instead of leaving
  useEffect(() => {
    const handlePopState = (event) => {
      if (currentStep > 0 && !showClientSelection) {
        event.preventDefault();
        setCurrentStep(prev => Math.max(0, prev - 1));
        window.history.pushState({ step: currentStep - 1 }, '');
      }
    };
    
    if (!showClientSelection) {
      window.history.pushState({ step: currentStep }, '');
      window.addEventListener('popstate', handlePopState);
    }
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentStep, showClientSelection]);
  
  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if ((formData.companyName || formData.employeeName) && !showClientSelection) {
        e.preventDefault();
        e.returnValue = 'You have unsaved progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData.companyName, formData.employeeName, showClientSelection]);

  // AUTO-SAVE: Persist form data and wizard state on every change (localStorage for session recovery)
  useEffect(() => {
    if (formData.companyName || formData.employeeName || formData.paystubs?.length > 0) {
      const sessionData = {
        formData,
        clientId: currentClientId,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(CURRENT_SESSION_KEY, JSON.stringify(sessionData));
      localStorage.setItem(WIZARD_STATE_KEY, JSON.stringify({ currentStep }));
    }
  }, [formData, currentStep, currentClientId]);

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    sessionStorage.setItem('paystub_authenticated', 'true');
  };

  const updateFormData = useCallback((updates) => {
    // Accept either a plain object OR an updater function so callers can read
    // the freshest state when computing the next slice. Critical when two
    // effects fire in the same render and the later one would otherwise
    // overwrite earlier updates with stale closure data.
    setFormData(prev => {
      const patch = typeof updates === 'function' ? updates(prev) : updates;
      return { ...prev, ...patch };
    });
  }, []);
  
  // Save current client to DATABASE (persists across deployments)
  const saveCurrentClient = useCallback(async (opts) => {
    const silent = opts?.silent === true;
    if (!formData.companyName && !formData.employeeName) {
      if (!silent) toast.error('Please fill in company or employee info first');
      return null;
    }
    
    if (!silent) setSavingClient(true);
    const clientName = `${formData.companyName || 'Company'} - ${formData.employeeName || 'Employee'}`;
    
    try {
      // Prepare data for database
      if (!currentClientId && !pendingClientIdRef.current) pendingClientIdRef.current = `paystub-${Date.now()}`;
      const clientId = currentClientId || pendingClientIdRef.current;
      const dbData = {
        id: clientId,
        employee_name: formData.employeeName,
        employee_ssn: formData.employeeSSN,
        employee_address: formData.employeeAddress,
        employee_city: formData.employeeCity,
        employee_state: formData.employeeState,
        employee_zip: formData.employeeZip,
        company_name: formData.companyName,
        company_address: formData.companyAddress,
        company_city: formData.companyCity,
        company_state: formData.companyState,
        company_zip: formData.companyZip,
        company_ein: formData.companyEIN,
        pay_frequency: formData.payFrequency,
        payment_type: formData.paymentType,
        hourly_rate: formData.hourlyRate,
        salary: formData.salary,
        tax_state: formData.taxState,
        filing_status: formData.filingStatus,
        number_of_stubs: formData.numberOfStubs,
        paystubs: formData.paystubs,
        template_id: formData.selectedTemplate,
        color_theme: formData.colorTheme,
        form_data: { ...formData },  // Store complete form data for full restoration
        record_generation: opts?.recordGeneration === true,
      };
      
      // Save to database with retry
      let response;
      let retries = 2;
      
      while (retries > 0) {
        try {
          response = await axios.post(`${API}/api/paystub-clients`, dbData, {
            timeout: 15000 // 15 second timeout
          });
          break; // Success, exit loop
        } catch (retryError) {
          retries--;
          if (retries === 0) throw retryError;
          console.log('Retrying save...');
          await new Promise(r => setTimeout(r, 1000)); // Wait 1 second before retry
        }
      }
      
      if (response.data.success) {
        // Update local state
        const clientData = {
          id: clientId,
          name: clientName,
          savedAt: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          formData: { ...formData }
        };
        
        const existed = savedClients.some(c => c.id === clientId);
        if (!currentClientId) setCurrentClientId(clientId);
        if (!silent) toast.success(existed ? `Updated "${clientName}" (saved to database)` : `Saved "${clientName}" to database!`);
        setSavedClients(prev => {
          const idx = prev.findIndex(c => c.id === clientId);
          const next = idx >= 0 ? prev.map((c, i) => (i === idx ? { ...c, ...clientData } : c)) : [...prev, clientData];
          localStorage.setItem(SAVED_CLIENTS_KEY, JSON.stringify(next));
          return next;
        });
        lastAutoSavedRef.current = JSON.stringify(formData);
        
        return clientId;
      }
    } catch (error) {
      console.error('Error saving client to database:', error);
      if (!silent) toast.error('Failed to save to database. Saving locally...');
      
      // Fallback to localStorage
      const clientData = {
        id: currentClientId || Date.now().toString(),
        name: clientName,
        savedAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        formData: { ...formData }
      };
      
      const existingIndex = savedClients.findIndex(c => c.id === currentClientId);
      let updatedClients;
      
      if (existingIndex >= 0) {
        updatedClients = [...savedClients];
        updatedClients[existingIndex] = clientData;
      } else {
        updatedClients = [...savedClients, clientData];
        setCurrentClientId(clientData.id);
      }
      
      setSavedClients(updatedClients);
      localStorage.setItem(SAVED_CLIENTS_KEY, JSON.stringify(updatedClients));
      
      return clientData.id;
    } finally {
      if (!silent) setSavingClient(false);
    }
  }, [formData, currentClientId, savedClients]);

  // Auto-save the client every time the step changes (silent; skips if nothing changed).
  useEffect(() => {
    if (!currentClientId) pendingClientIdRef.current = null;
  }, [currentClientId]);

  useEffect(() => {
    if (showClientSelection) return;
    if (!formData.companyName && !formData.employeeName) return;
    if (lastAutoSavedRef.current === JSON.stringify(formData)) return;
    saveCurrentClient({ silent: true });
  }, [currentStep]);
  
  // 2026-02-28 (user request) — Legacy intake migration.  Clients that
  // were imported from the public intake form BEFORE the new per-check
  // picker shipped are missing `payEntryMode`, `perCheckAmount`, and
  // `perCheckType`.  Infer them on the fly from the fields the legacy
  // import DID write (`amountType`, first stub `current.grossPay`,
  // `payFrequency`) so the admin lands on step 4 with the new picker
  // already pre-filled instead of showing \$0.  Also writes back
  // `annualSalary` when missing so flipping to the annual path keeps a
  // sensible value.
  const _migrateLegacyIntake = useCallback((fd) => {
    if (!fd || fd.payEntryMode) return fd;    // already on new shape
    const first = Array.isArray(fd.paystubs) && fd.paystubs.length
      ? fd.paystubs[0] : null;
    const perCheckGross = first?.current?.grossPay || fd.salary || 0;
    if (!(perCheckGross > 0)) return fd;      // nothing to infer from
    const periodsPerYear = {
      'weekly': 52, 'bi-weekly': 26, 'biweekly': 26,
      'semi-monthly': 24, 'semimonthly': 24, 'monthly': 12,
    }[fd.payFrequency] || 26;
    return {
      ...fd,
      payEntryMode: 'per-check',
      perCheckAmount: Number(perCheckGross),
      perCheckType: (fd.amountType === 'net') ? 'net' : 'gross',
      annualSalary: fd.annualSalary || Number((perCheckGross * periodsPerYear).toFixed(2)),
    };
  }, []);

  // Load a saved client
  const loadClient = useCallback((client) => {
    setFormData(_migrateLegacyIntake(client.formData));
    setCurrentClientId(client.id);
    setShowClientSelection(false);
    setCurrentStep(0); // Start at template selection
    toast.success(`Loaded "${client.name}"`);
  }, [_migrateLegacyIntake]);
  
  // Create new client (reset form)
  const createNewClient = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentClientId(null);
    setShowClientSelection(false);
    setCurrentStep(0);
    // Clear session
    localStorage.removeItem(CURRENT_SESSION_KEY);
    localStorage.removeItem(WIZARD_STATE_KEY);
    toast.success('Starting new paystub');
  }, []);
  
  // Load client and jump to Paystub Details for quick regeneration
  const quickGenerate = useCallback((client, e) => {
    e.stopPropagation();
    setFormData(_migrateLegacyIntake(client.formData));
    setCurrentClientId(client.id);
    setShowClientSelection(false);
    setCurrentStep(4); // Jump to Paystub Details to update dates/amounts
    toast.success(`Loaded "${client.name}" — update dates & amounts, then generate!`);
  }, [_migrateLegacyIntake]);
  
  // Delete a saved client from DATABASE (moves to trash)
  const deleteClient = useCallback(async (clientId, e) => {
    e.stopPropagation();
    
    try {
      // Delete from database (soft delete - moves to trash)
      await axios.delete(`${API}/api/paystub-clients/${clientId}`);
      
      // Update local state
      const updatedClients = savedClients.filter(c => c.id !== clientId);
      setSavedClients(updatedClients);
      localStorage.setItem(SAVED_CLIENTS_KEY, JSON.stringify(updatedClients));
      toast.success('Client moved to trash');
    } catch (error) {
      console.error('Error deleting client:', error);
      // Still remove from local state
      const updatedClients = savedClients.filter(c => c.id !== clientId);
      setSavedClients(updatedClients);
      localStorage.setItem(SAVED_CLIENTS_KEY, JSON.stringify(updatedClients));
      toast.success('Client moved to trash');
    }
  }, [savedClients]);

  // Fetch trashed clients
  const fetchTrashedClients = useCallback(async () => {
    setLoadingTrash(true);
    try {
      const response = await axios.get(`${API}/api/paystub-clients-trash`);
      if (response.data.success) {
        const clients = response.data.clients.map(c => ({
          id: c.id,
          name: `${c.company_name || 'Company'} - ${c.employee_name || 'Employee'}`,
          deletedAt: c.deleted_at,
          formData: c.form_data || {}
        }));
        setTrashedClients(clients);
      }
    } catch (e) {
      console.error('Error fetching trashed clients:', e);
      toast.error('Failed to load trash');
    } finally {
      setLoadingTrash(false);
    }
  }, []);

  // Restore client from trash
  const restoreClient = useCallback(async (clientId) => {
    try {
      await axios.post(`${API}/api/paystub-clients/${clientId}/restore`);
      
      // Refresh both lists
      const response = await axios.get(`${API}/api/paystub-clients`);
      if (response.data.success) {
        const clients = response.data.clients.map(c => ({
          id: c.id,
          name: `${c.company_name || 'Company'} - ${c.employee_name || 'Employee'}`,
          savedAt: c.created_at,
          lastModified: c.updated_at,
          formData: c.form_data || {}
        }));
        setSavedClients(clients);
      }
      
      // Refresh trash
      await fetchTrashedClients();
      toast.success('Client restored from trash');
    } catch (error) {
      console.error('Error restoring client:', error);
      toast.error('Failed to restore client');
    }
  }, [fetchTrashedClients]);

  // Permanently delete from trash
  const permanentDelete = useCallback(async (clientId) => {
    try {
      await axios.delete(`${API}/api/paystub-clients/${clientId}?permanent=true`);
      setTrashedClients(prev => prev.filter(c => c.id !== clientId));
      toast.success('Client permanently deleted');
    } catch (error) {
      console.error('Error permanently deleting client:', error);
      toast.error('Failed to delete client');
    }
  }, []);

  // Empty entire trash
  const emptyTrash = useCallback(async () => {
    if (!window.confirm('Are you sure you want to permanently delete ALL clients in trash? This cannot be undone.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/api/paystub-clients-trash/empty`);
      setTrashedClients([]);
      toast.success(`Permanently deleted ${response.data.deleted_count} clients`);
    } catch (error) {
      console.error('Error emptying trash:', error);
      toast.error('Failed to empty trash');
    }
  }, []);

  // Fetch generation history
  const fetchGenerationHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/api/paystub-generation-history?days=30&limit=50`);
      if (response.data.success) {
        setGenerationHistory(response.data.logs || []);
      }
    } catch (e) {
      console.error('Error fetching generation history:', e);
      toast.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const handleTemplateSelect = (templateId) => {
    updateFormData({ selectedTemplate: templateId });
  };

  const handleColorChange = (color) => {
    updateFormData({ colorScheme: color });
  };

  // ── Required-field gate (Feb 2026) ────────────────────────────────
  // Vital fields the user must fill before advancing.  Every field can
  // be explicitly bypassed by adding it to `skippedFields` — which the
  // step components toggle via a "Not needed" chip beside the input.
  const STEP_REQUIRED_FIELDS = {
    1: [ // Company Info
      { key: 'companyName',     label: 'Company Name' },
      { key: 'companyAddress1', label: 'Company Address' },
      { key: 'companyCity',     label: 'Company City' },
      { key: 'companyState',    label: 'Company State' },
      { key: 'companyZip',      label: 'Company ZIP' },
    ],
    2: [ // Employee Info
      { key: 'employeeName',     label: 'Employee Name' },
      { key: 'employeeAddress1', label: 'Employee Address' },
      { key: 'employeeCity',     label: 'Employee City' },
      { key: 'employeeState',    label: 'Employee State' },
      { key: 'employeeZip',      label: 'Employee ZIP' },
      { key: 'employeeSSN',      label: 'Last 4 of SSN' },
    ],
  };

  const [skippedFields, setSkippedFields] = React.useState({});
  const [attemptedNext, setAttemptedNext] = React.useState(false);
  const [missingFields, setMissingFields] = React.useState([]);

  const computeMissingForStep = React.useCallback((step) => {
    const required = STEP_REQUIRED_FIELDS[step] || [];
    // Paystub Details (step 4) — per-stub check number required
    const missing = required.filter(({ key }) => {
      if (skippedFields[key]) return false;
      const v = formData[key];
      return v === undefined || v === null || String(v).trim() === '';
    });
    if (step === 4) {
      // Every paystub row must have a check number + pay date + grossPay
      const stubs = Array.isArray(formData.paystubs) ? formData.paystubs : [];
      stubs.forEach((s, i) => {
        if (!skippedFields[`stub${i}_checkNumber`] && !String(s?.checkNumber ?? '').trim()) {
          missing.push({ key: `stub${i}_checkNumber`, label: `Stub ${i + 1} Check #` });
        }
        if (!skippedFields[`stub${i}_payDate`] && !String(s?.payDate ?? '').trim()) {
          missing.push({ key: `stub${i}_payDate`, label: `Stub ${i + 1} Pay Date` });
        }
      });
    }
    return missing;
  }, [formData, skippedFields]);

  React.useEffect(() => {
    // Live-recompute so red borders auto-clear as the user types
    if (attemptedNext) setMissingFields(computeMissingForStep(currentStep));
  }, [formData, skippedFields, attemptedNext, currentStep, computeMissingForStep]);

  const toggleSkipField = React.useCallback((key) => {
    setSkippedFields((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleNext = () => {
    // Step 0: Template Selection - This one is required
    if (currentStep === 0 && !formData.selectedTemplate) {
      toast.error('Please select a template first');
      return;
    }

    // Steps 1, 2, 4 — validate required fields
    const missing = computeMissingForStep(currentStep);
    if (missing.length > 0) {
      setAttemptedNext(true);
      setMissingFields(missing);
      toast.error(
        `Fill in: ${missing.map(m => m.label).join(', ')} — or tap "Not needed" beside any field that doesn't apply.`,
        { duration: 5000 }
      );
      return;
    }
    setAttemptedNext(false);
    setMissingFields([]);

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canJumpAnywhere = !!currentClientId && (formData.paystubs?.length || 0) > 0;
  const handleStepClick = (stepId) => {
    if (stepId <= currentStep || stepId === 0 || canJumpAnywhere) {
      setCurrentStep(stepId);
    }
  };

  const handleExit = () => {
    // Save current client before exiting if there's data
    if (formData.companyName || formData.employeeName) {
      saveCurrentClient();
    }
    
    sessionStorage.removeItem('paystub_authenticated');
    if (onExit) onExit();
  };

  // Toggle favorite status for a client
  const toggleFavorite = useCallback((clientId, e) => {
    e.stopPropagation();
    setFavoriteClients(prev => {
      const newFavorites = prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId];
      localStorage.setItem('paystub_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
    toast.success(favoriteClients.includes(clientId) ? 'Removed from favorites' : 'Added to favorites');
  }, [favoriteClients]);

  // Duplicate a client (create a copy with new ID)
  const duplicateClient = useCallback(async (client, e) => {
    e.stopPropagation();
    
    try {
      const newId = `paystub-${Date.now()}`;
      const newClient = {
        ...client,
        id: newId,
        name: `${client.name} (Copy)`,
        savedAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        formData: {
          ...client.formData,
          employeeName: client.formData?.employeeName ? `${client.formData.employeeName} (Copy)` : ''
        }
      };
      
      // Save to database
      await axios.post(`${API}/api/paystub-clients`, {
        id: newId,
        employee_name: newClient.formData?.employeeName || '',
        company_name: newClient.formData?.companyName || '',
        form_data: newClient.formData
      });
      
      // Update local state
      setSavedClients(prev => [newClient, ...prev]);
      toast.success('Client duplicated! You can now edit the copy.');
    } catch (error) {
      console.error('Error duplicating client:', error);
      toast.error('Failed to duplicate client');
    }
  }, []);

  // Filter and sort clients
  const filteredAndSortedClients = React.useMemo(() => {
    let filtered = savedClients;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(client => 
        client.name.toLowerCase().includes(query) ||
        (client.formData?.companyName || '').toLowerCase().includes(query) ||
        (client.formData?.employeeName || '').toLowerCase().includes(query)
      );
    }
    
    // Sort - favorites first, then by selected criteria
    return filtered.sort((a, b) => {
      // Favorites always first
      const aFav = favoriteClients.includes(a.id);
      const bFav = favoriteClients.includes(b.id);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      
      // Then sort by selected criteria
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'lastModified') {
        return new Date(b.lastModified || b.savedAt) - new Date(a.lastModified || a.savedAt);
      } else {
        return new Date(b.savedAt) - new Date(a.savedAt);
      }
    });
  }, [savedClients, searchQuery, sortBy, favoriteClients]);

  // Calculate quick stats from generation history
  const quickStats = React.useMemo(() => {
    if (!generationHistory.length) return null;
    
    const totalGenerated = generationHistory.length;
    const totalGross = generationHistory.reduce((sum, log) => sum + (log.total_gross || 0), 0);
    const totalNet = generationHistory.reduce((sum, log) => sum + (log.total_net || 0), 0);
    const uniqueCompanies = new Set(generationHistory.map(log => log.company_name)).size;
    
    return { totalGenerated, totalGross, totalNet, uniqueCompanies };
  }, [generationHistory]);
  
  // Handle successful download - save client automatically
  const handleDownloadSuccess = useCallback(() => {
    saveCurrentClient({ recordGeneration: true });
    if (formData.companyName) commitCheckNumbers(formData.companyName, (formData.paystubs || []).map(st => st.checkNumber)).catch(() => {});
    // 2026-06-12: per user, after a paystub is generated, prompt the operator
    // to optionally roll those numbers into a W-2.  W-2 is now ANNUALIZED
    // Jan-Dec from the paystub's per-period gross (× pay periods/year), so
    // we only need a positive current-period gross to enable it.
    //
    // 2026-02-08 mobile fix: on iOS Safari a blob-URL download replaces the
    // tab, so a deferred setTimeout(setState) never runs and the prompt
    // never appears. Set the modal state SYNCHRONOUSLY inside the same
    // click handler so it's committed to the DOM before the browser hands
    // control to the PDF viewer.
    const latest = (formData.paystubs || []).slice(-1)[0];
    const sourceGross = latest?.grossPay || latest?.ytd?.grossPay || 0;
    if (latest && sourceGross > 0) {
      setW2PromptOpen(true);
    }
  }, [saveCurrentClient, formData.paystubs, formData.companyName]);

  // Handle extracted data from uploaded paystub
  const handleUploadExtracted = useCallback((result) => {
    if (result.mode === 'update') {
      setRedateSource(result);
      setShowUploadScreen(false);
      return;
    }
    if (result.mode === 'template') {
      // Use the extracted data with a template
      setFormData(prev => ({
        ...prev,
        ...result.formData
      }));
      setUploadedPaystubData(result);
      setShowUploadScreen(false);
      setShowClientSelection(false);
      setCurrentStep(0); // Go to template selection
      toast.success('Data loaded! Now select a template');
    } else if (result.mode === 'replica') {
      // Store the data for replica generation
      setUploadedPaystubData(result);
      setShowUploadScreen(false);
      setShowClientSelection(false);
      // TODO: Navigate to replica preview/edit screen
      toast.success('Ready to generate exact replica');
    }
  }, []);

  if (!isAuthenticated) {
    return <PinProtection onAuthenticated={handleAuthenticated} />;
  }

  if (showW2Studio) {
    return (
      <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <W2Studio savedClients={savedClients} onBack={() => setShowW2Studio(false)} />
        </div>
      </div>
    );
  }

  if (overviewClient) {
    return (
      <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <ClientOverview
            client={overviewClient}
            onBack={() => setOverviewClient(null)}
            onEdit={(step) => {
              const fd = _migrateLegacyIntake(overviewClient.formData);
              setFormData(fd);
              lastAutoSavedRef.current = JSON.stringify(fd);
              setCurrentClientId(overviewClient.id);
              setShowClientSelection(false);
              setCurrentStep(step);
              setOverviewClient(null);
            }}
          />
        </div>
      </div>
    );
  }

  if (stubHistoryClient) {
    return (
      <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <ClientStubHistory
            client={stubHistoryClient}
            onBack={() => setStubHistoryClient(null)}
            onApply={(fd, keepTemplate) => {
              setFormData({ ..._migrateLegacyIntake(stubHistoryClient.formData), ...fd });
              setCurrentClientId(stubHistoryClient.id);
              setStubHistoryClient(null);
              setShowClientSelection(false);
              setCurrentStep(keepTemplate ? 4 : 0);
              toast.success(keepTemplate ? 'New dates applied — review the numbers, then generate.' : 'New dates applied — pick a template.');
            }}
          />
        </div>
      </div>
    );
  }

  if (redateSource) {
    return (
      <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <PaystubRedatePanel
            source={redateSource}
            onBack={() => { setRedateSource(null); setShowUploadScreen(true); }}
            onApply={(fd) => {
              setFormData({ ...INITIAL_FORM_DATA, ...fd });
              setCurrentClientId(null);
              setRedateSource(null);
              setShowClientSelection(false);
              setCurrentStep(0);
              toast.success('New dates applied — suggested template selected. Review each step, then generate.');
            }}
          />
        </div>
      </div>
    );
  }

  // Show upload screen
  if (showUploadScreen) {
    return (
      <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <UploadPaystubStep
            onExtracted={handleUploadExtracted}
            onCancel={() => { setShowUploadScreen(false); setUploadUpdateMode(false); }}
            updateMode={uploadUpdateMode}
          />
        </div>
      </div>
    );
  }

  // Show client selection screen on entry
  if (showClientSelection) {
    return (
      <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={handleExit}
                className="text-zinc-400 hover:text-white"
                data-testid="exit-wizard-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Exit
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Payday ST</h1>
                <p className="text-sm text-zinc-400">Choose a client or create new</p>
              </div>
            </div>
          </div>

          <PaydayHub
            onSelect={(id) => {
              if (id === 'create-paystub') createNewClient();
              else if (id === 'create-w2') setShowW2Studio(true);
              else { setUploadUpdateMode(true); setShowUploadScreen(true); }
            }}
          />

          {/* Client Selection */}
          <Card className="bg-zinc-900 border-zinc-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <Users className="w-6 h-6 text-amber-400" />
                Select Client
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 2026-02 — Pending intakes banner + quick-share */}
              <IntakeFrontPanel
                onOpenModal={() => setShowIntakeModal(true)}
                onImported={async (newClientId) => {
                  await loadSavedClients();
                  try {
                    const resp = await axios.get(`${API}/api/paystub-clients`);
                    if (resp.data.success) {
                      const c = (resp.data.clients || []).find((x) => x.id === newClientId);
                      if (c) {
                        setFormData(_migrateLegacyIntake(c.form_data || {}));
                        setCurrentClientId(c.id);
                        setShowClientSelection(false);
                        setCurrentStep(1);
                        toast.success('Intake imported — review Company Info, then step forward');
                      }
                    }
                  } catch (e) { /* silent */ }
                }}
              />

              {/* Create New Button */}
              <Button
                onClick={createNewClient}
                className="w-full h-16 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-lg font-semibold"
                data-testid="create-new-client-btn"
              >
                <Plus className="w-6 h-6 mr-3" />
                Create New Payday ST
              </Button>
              
              {/* Upload Existing Payday ST Button */}
              <Button
                onClick={() => { setUploadUpdateMode(false); setShowUploadScreen(true); }}
                className="w-full h-16 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-lg font-semibold"
                data-testid="upload-paystub-btn"
              >
                <Upload className="w-6 h-6 mr-3" />
                Upload Existing Payday ST
              </Button>
              
              {/* Saved Clients */}
              {savedClients.length > 0 && (
                <>
                  <div className="flex items-center gap-3 py-4">
                    <div className="flex-1 h-px bg-zinc-700"></div>
                    <span className="text-zinc-500 text-sm">Or continue with saved client</span>
                    <div className="flex-1 h-px bg-zinc-700"></div>
                  </div>
                  
                  <ClientButtonKey open={showButtonKey} onToggle={() => setShowButtonKey((v) => !v)} />
                  {/* Search and Filter Bar */}
                  <div className="flex gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="text"
                        placeholder="Search clients..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                    >
                      <option value="lastModified">Recently Edited</option>
                      <option value="created">Recently Created</option>
                      <option value="name">Name A-Z</option>
                    </select>
                  </div>
                  
                  {/* Quick Stats (if history loaded) */}
                  {quickStats && (
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="p-3 bg-zinc-800 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-400">{quickStats.totalGenerated}</p>
                        <p className="text-xs text-zinc-500">Total Generated</p>
                      </div>
                      <div className="p-3 bg-zinc-800 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-400">${(quickStats.totalGross/1000).toFixed(1)}k</p>
                        <p className="text-xs text-zinc-500">Total Gross</p>
                      </div>
                      <div className="p-3 bg-zinc-800 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-400">${(quickStats.totalNet/1000).toFixed(1)}k</p>
                        <p className="text-xs text-zinc-500">Total Net</p>
                      </div>
                      <div className="p-3 bg-zinc-800 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-400">{quickStats.uniqueCompanies}</p>
                        <p className="text-xs text-zinc-500">Companies</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid gap-3">
                    {filteredAndSortedClients.length === 0 ? (
                      <div className="text-center py-6">
                        <Search className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                        <p className="text-zinc-400">No clients match "{searchQuery}"</p>
                      </div>
                    ) : (
                      filteredAndSortedClients.map((client) => {
                        const isFavorite = favoriteClients.includes(client.id);
                        return (
                          <div
                            key={client.id}
                            onClick={() => setOverviewClient(client)}
                            className={`flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 p-4 bg-zinc-800 rounded-lg border transition-all group cursor-pointer ${
                              isFavorite 
                                ? 'border-amber-500/50 bg-amber-500/5' 
                                : 'border-zinc-700 hover:border-amber-500'
                            }`}
                            data-testid={`client-${client.id}`}
                          >
                            <div className="flex items-center gap-4 min-w-0">
                              <div className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center ${
                                isFavorite ? 'bg-amber-500/30' : 'bg-amber-600/20'
                              }`}>
                                {isFavorite ? (
                                  <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                                ) : (
                                  <User className="w-6 h-6 text-amber-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-white font-medium text-lg break-words">{client.name}</p>
                                <div className="flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
                                  <Clock className="w-3 h-3" />
                                  Last edited: {new Date(client.lastModified || client.savedAt).toLocaleDateString()}
                                  {savedW2sByClient[client.id] > 0 && (
                                    <span
                                      className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-semibold"
                                      data-testid={`w2-count-${client.id}`}
                                      title="Saved W-2s — open the client to add another year"
                                    >
                                      {savedW2sByClient[client.id]} W-2{savedW2sByClient[client.id] > 1 ? 's' : ''} saved
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <ClientRowActions
                              client={client}
                              isFavorite={isFavorite}
                              hasStubs={client.formData?.paystubs?.length > 0}
                              handlers={{
                                favorite: (e) => toggleFavorite(client.id, e),
                                duplicate: (e) => duplicateClient(client, e),
                                next: () => setStubHistoryClient(client),
                                redo: (e) => quickGenerate(client, e),
                                edit: () => setOverviewClient(client),
                                delete: (e) => deleteClient(client.id, e),
                              }}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
              
              {!loadingClients && savedClients.length === 0 && (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">No saved clients yet</p>
                  <p className="text-sm text-zinc-500">Your clients will be saved to the database after generating paystubs</p>
                </div>
              )}
              
              {loadingClients && (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-amber-400 mx-auto mb-3 animate-spin" />
                  <p className="text-zinc-400">Loading saved clients from database...</p>
                </div>
              )}
              
              {/* Database indicator */}
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-zinc-500">
                <Database className="w-3 h-3" />
                <span>Clients are saved to database and persist across deployments</span>
              </div>
              
              {/* Trash and History Buttons */}
              <div className="mt-6 pt-4 border-t border-zinc-700 flex gap-3 justify-center flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => setShowIntakeModal(true)}
                  className="border-fuchsia-600 text-fuchsia-300 hover:text-white hover:border-fuchsia-500 bg-fuchsia-500/10"
                  data-testid="intake-open-modal"
                >
                  🔗 Client Intake Link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTrash(true);
                    setShowHistory(false);
                    fetchTrashedClients();
                  }}
                  className="border-zinc-600 text-zinc-400 hover:text-white hover:border-red-500"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Trash ({trashedClients.length})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowHistory(true);
                    setShowTrash(false);
                    fetchGenerationHistory();
                  }}
                  className="border-zinc-600 text-zinc-400 hover:text-white hover:border-blue-500"
                >
                  <History className="w-4 h-4 mr-2" />
                  Generation Log
                </Button>
              </div>
              {showIntakeModal && (
                <IntakeLinkModal
                  onClose={() => setShowIntakeModal(false)}
                  onImported={async (newClientId) => {
                    // 1. Refresh the saved-client list
                    await loadSavedClients();
                    // 2. Fetch the just-imported client and jump straight
                    //    into the wizard at Company Info (step 1) so the
                    //    user can review page by page.
                    try {
                      const resp = await axios.get(`${API}/api/paystub-clients`);
                      if (resp.data.success) {
                        const c = (resp.data.clients || []).find((x) => x.id === newClientId);
                        if (c) {
                          setFormData(_migrateLegacyIntake(c.form_data || {}));
                          setCurrentClientId(c.id);
                          setShowClientSelection(false);
                          setShowIntakeModal(false);
                          setCurrentStep(1);   // 0=Template, 1=Company Info
                          toast.success('Intake imported — review Company Info, then step forward');
                        }
                      }
                    } catch (e) { /* silent */ }
                  }}
                />
              )}
            </CardContent>
          </Card>
          
          {/* TRASH PANEL */}
          {showTrash && (
            <Card className="bg-zinc-900 border-red-500/30 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-3">
                    <Trash2 className="w-6 h-6 text-red-400" />
                    Trash
                  </CardTitle>
                  <div className="flex gap-2">
                    {trashedClients.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={emptyTrash}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        Empty Trash
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowTrash(false)}
                      className="text-zinc-400"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingTrash ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-red-400 mx-auto mb-3 animate-spin" />
                    <p className="text-zinc-400">Loading trash...</p>
                  </div>
                ) : trashedClients.length === 0 ? (
                  <div className="text-center py-8">
                    <Trash2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">Trash is empty</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {trashedClients.map((client) => (
                      <div
                        key={client.id}
                        className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-red-500/30"
                      >
                        <div>
                          <p className="text-white font-medium">{client.name}</p>
                          <div className="flex items-center gap-2 text-xs text-zinc-500">
                            <Clock className="w-3 h-3" />
                            Deleted: {new Date(client.deletedAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => restoreClient(client.id)}
                            className="border-green-500 text-green-400 hover:bg-green-500/10"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              if (window.confirm('Permanently delete this client? This cannot be undone.')) {
                                permanentDelete(client.id);
                              }
                            }}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete Forever
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* GENERATION HISTORY PANEL */}
          {showHistory && (
            <Card className="bg-zinc-900 border-blue-500/30 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-3">
                    <History className="w-6 h-6 text-blue-400" />
                    Generation History (Last 30 Days)
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistory(false)}
                    className="text-zinc-400"
                  >
                    Close
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-blue-400 mx-auto mb-3 animate-spin" />
                    <p className="text-zinc-400">Loading history...</p>
                  </div>
                ) : generationHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">No generation history yet</p>
                  </div>
                ) : (
                  <div className="grid gap-3 max-h-[400px] overflow-y-auto">
                    {generationHistory.map((log) => (
                      <div
                        key={log.id}
                        className="p-4 bg-zinc-800 rounded-lg border border-zinc-700"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-medium">
                            {log.company_name || 'Company'} - {log.employee_name || 'Employee'}
                          </p>
                          <Badge variant="outline" className="text-blue-400 border-blue-500">
                            {log.num_stubs} stub{log.num_stubs !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-zinc-400">
                          <div>
                            <Clock className="w-3 h-3 inline mr-1" />
                            {new Date(log.timestamp).toLocaleString()}
                          </div>
                          <div>
                            Gross: ${log.total_gross?.toLocaleString() || 0}
                          </div>
                          <div>
                            Net: ${log.total_net?.toLocaleString() || 0}
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-zinc-500">
                          Template: {log.template_id} | Frequency: {log.pay_frequency}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-zinc-500 mt-4 text-center">
                  Generation logs are permanent and never auto-deleted
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <TemplateGallery 
            selectedTemplate={formData.selectedTemplate}
            onSelectTemplate={handleTemplateSelect}
            colorScheme={formData.colorScheme}
            onColorChange={handleColorChange}
          />
        );
      case 1:
        return <CompanyInfoStep formData={formData} updateFormData={updateFormData}
                 missingFields={missingFields} skippedFields={skippedFields}
                 onToggleSkip={toggleSkipField} attemptedNext={attemptedNext} />;
      case 2:
        return <EmployeeInfoStep formData={formData} updateFormData={updateFormData}
                 missingFields={missingFields} skippedFields={skippedFields}
                 onToggleSkip={toggleSkipField} attemptedNext={attemptedNext} />;
      case 3:
        return <EmploymentDetailsStep formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <PaystubDetailsStep formData={formData} updateFormData={updateFormData}
                 missingFields={missingFields} skippedFields={skippedFields}
                 onToggleSkip={toggleSkipField} attemptedNext={attemptedNext}
                 onSaveClient={saveCurrentClient} />;
      case 5:
        return <PreviewStep formData={formData} updateFormData={updateFormData} onDownloadSuccess={handleDownloadSuccess} onEditValues={() => setCurrentStep(4)} />;
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const selectedTemplateName = PAYSTUB_TEMPLATES.find(t => t.id === formData.selectedTemplate)?.name;

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleExit}
              className="text-zinc-400 hover:text-white"
              data-testid="exit-wizard-btn"
            >
              <Home className="w-5 h-5 mr-2" />
              Exit
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Payday ST</h1>
              <p className="text-sm text-zinc-400">
                {currentClientId ? 'Editing saved client' : 'Generate professional paystubs'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Back to Client Selection */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (formData.companyName || formData.employeeName) {
                  saveCurrentClient();
                }
                setShowClientSelection(true);
              }}
              className="border-zinc-600 text-zinc-300 hover:text-white"
              data-testid="back-to-clients-btn"
            >
              <Users className="w-4 h-4 mr-1" />
              Clients
            </Button>
            
            {/* Save Button */}
            {(formData.companyName || formData.employeeName) && (
              <Button
                variant="outline"
                size="sm"
                onClick={saveCurrentClient}
                className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
                data-testid="save-client-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
            )}
            
            {formData.selectedTemplate && (
              <>
                <Badge 
                  className="text-white"
                  style={{ 
                    backgroundColor: formData.colorScheme === 'darkgrey' ? '#4b5563' : '#27272a'
                  }}
                >
                  {(formData.colorScheme || 'default').toUpperCase()}
                </Badge>
                <Badge className="bg-blue-600 text-white">
                  {selectedTemplateName}
                </Badge>
              </>
            )}
            <Badge className="bg-amber-600 text-white">
              Step {currentStep + 1} of {STEPS.length}
            </Badge>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2 bg-zinc-800" />
        </div>

        {/* Step Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isClickable = step.id <= currentStep || canJumpAnywhere;
            
            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(step.id)}
                disabled={!isClickable}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                  isActive
                    ? 'bg-amber-600 text-white'
                    : isCompleted
                      ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30 cursor-pointer'
                      : isClickable
                        ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 cursor-pointer'
                        : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
                data-testid={`step-nav-${step.id}`}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
                <span className="hidden md:inline">{step.title}</span>
              </button>
            );
          })}
        </div>

        {/* 2026-02-08 — Saved W-2s for this client. Renders above the
            wizard steps whenever the loaded client has one or more W-2s
            on file. Tapping "Add another year" seeds the W-2 modal with
            the selected year's values so the user only edits what changed. */}
        {currentClientId && savedW2s.length > 0 && (
          <Card className="bg-zinc-900 border-emerald-500/40 mb-4" data-testid="saved-w2-card">
            <CardContent className="py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-bold text-emerald-300">Saved W-2s for this client</span>
                  <span className="text-[10px] text-zinc-500">({savedW2s.length} on file)</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {savedW2s.map((w) => (
                  <div key={w.id} className="flex items-center gap-1 rounded-md border border-emerald-600/40 bg-zinc-950 pl-2 pr-1 py-1">
                    <span className="text-xs text-emerald-200 font-semibold">
                      {w.tax_year} · {w.employer_name || 'Employer'}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const cy = new Date().getFullYear();
                        const seedYear = Number(w.tax_year) || cy - 1;
                        const suggested = seedYear >= cy ? cy : seedYear + 1;
                        setW2SeedFromSaved(w);
                        setW2Years([]);
                        setW2YearIndex(0);
                        setTaxFormYear(suggested);
                        setW2ModalOpen(true);
                      }}
                      className="ml-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white"
                      data-testid={`w2-add-year-${w.id}`}
                      title={`Reuse ${w.tax_year} numbers for a brand-new year (editable)`}
                    >
                      + Another year
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`Delete saved W-2 for ${w.tax_year}?`)) return;
                        try {
                          await axios.delete(`${API}/api/w2-saved/${w.id}`);
                          refreshSavedW2sForClient(currentClientId);
                          refreshAllSavedW2Counts();
                          toast.success('Saved W-2 removed');
                        } catch (e) {
                          toast.error('Failed to delete');
                        }
                      }}
                      className="ml-0.5 p-1 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                      title="Remove this saved W-2"
                      data-testid={`w2-delete-${w.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step Content */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-3">
              {React.createElement(STEPS[currentStep].icon, { className: 'w-6 h-6 text-amber-400' })}
              {STEPS[currentStep].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Floating Intake Reference Sidebar — visible on every wizard
            step (Feb 2026). Shows every field the client submitted via
            the intake form so the admin never has to hunt for a value.
            Only renders when this client was imported from an intake. */}
        <IntakeReferenceSidebar formData={formData} />

        {/* Navigation Buttons - MOVED TO LEFT SIDE to avoid Emergent logo */}
        <div className="flex justify-start gap-4 pb-20">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="border-zinc-600 text-zinc-300"
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="next-step-btn"
            >
              {currentStep === 0 ? (
                formData.selectedTemplate ? 'Continue with Template' : 'Select a Template'
              ) : (
                'Next'
              )}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => {
                const downloadBtn = document.querySelector('[data-testid="download-paystubs-btn"]');
                if (downloadBtn) downloadBtn.click();
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="final-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Paystubs
            </Button>
          )}
        </div>
      </div>

      {/* 2026-06-12: Post-paystub prompt. Auto-opens after download.
          Offers BOTH W-2 and 1099-NEC generation paths from the same paystub
          numbers (annualized Jan-Dec). User can pick either, both, or neither. */}
      {w2PromptOpen && (
        <div className="fixed inset-0 z-[68] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="w2-prompt">
          <div className="bg-zinc-950 border border-emerald-500/40 rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40">
                <FileCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Generate a tax form?</h3>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-4">
              Want to also build a <span className="text-emerald-300 font-semibold">W-2</span> (employee) or{" "}
              <span className="text-amber-300 font-semibold">1099-NEC</span> (contractor) from this paystub?
              Numbers will be annualized January through December for the year(s) you pick.
            </p>

            {/* ── How many W-2s? (1-4, 2026-02-08 user request) ── */}
            <div className="mb-3">
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">
                How many W-2s?
              </label>
              <div className="flex gap-1.5" data-testid="w2-count-picker">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      setW2Count(n);
                      // Extend / trim the selected-years array to match count.
                      const cy = new Date().getFullYear();
                      setW2SelectedYears((prev) => {
                        const next = [...prev];
                        while (next.length < n) next.push(cy - next.length - 1);
                        return next.slice(0, n);
                      });
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold border transition ${
                      w2Count === n
                        ? 'bg-emerald-600 border-emerald-400 text-white'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-emerald-500/40'
                    }`}
                    data-testid={`w2-count-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Which years? ── */}
            <div className="mb-4">
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">
                Tax year{w2Count > 1 ? 's' : ''} (edit numbers per year in the next step)
              </label>
              <div className="grid grid-cols-1 gap-1.5" data-testid="w2-years-picker">
                {Array.from({ length: w2Count }, (_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 w-8">#{i + 1}</span>
                    <select
                      value={w2SelectedYears[i] || new Date().getFullYear() - 1}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setW2SelectedYears((prev) => {
                          const next = [...prev];
                          next[i] = y;
                          return next;
                        });
                      }}
                      className="flex-1 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      data-testid={`w2-year-${i}`}
                    >
                      {Array.from({ length: 7 }, (_, k) => new Date().getFullYear() - k).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => {
                  // Kick off multi-year W-2 generation.  Open the modal
                  // for the first year; W2CreatorModal.onFinished will
                  // pop us to the next year automatically.
                  const queue = w2SelectedYears.slice(0, w2Count);
                  if (queue.length === 0) return;
                  setW2Years(queue);
                  setW2YearIndex(0);
                  setTaxFormYear(queue[0]);
                  setW2SeedFromSaved(null);
                  setW2PromptOpen(false);
                  setW2ModalOpen(true);
                }}
                className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center justify-center gap-2"
                data-testid="w2-prompt-yes"
              >
                <FileCheck className="w-4 h-4" />
                Create {w2Count} W-2{w2Count > 1 ? 's' : ''}
              </button>
              <button
                onClick={() => {
                  // 1099-NEC keeps its single-year flow for now.
                  setTaxFormYear(w2SelectedYears[0] || new Date().getFullYear() - 1);
                  setW2PromptOpen(false);
                  setNecModalOpen(true);
                }}
                className="px-4 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold flex items-center justify-center gap-2"
                data-testid="nec-prompt-yes"
              >
                <FileCheck className="w-4 h-4" />
                Create 1099-NEC ({w2SelectedYears[0] || new Date().getFullYear() - 1})
              </button>
            </div>

            {/* 2026-02-08 — "Add another year" per user request. If this client
                already has one or more saved W-2s, offer to reuse any of them
                as the seed for a brand-new tax year. Everything is pre-filled;
                the user only tweaks whatever changed. */}
            {savedW2s.length > 0 && (
              <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="w2-saved-strip">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">
                    Prior W-2s for this client — tap to add another year
                  </span>
                  <span className="text-[10px] text-emerald-200/70">{savedW2s.length} saved</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {savedW2s.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => {
                        // Suggest the current year first, then the year
                        // AFTER the seed's year, so the user is nudged to
                        // fill in a fresh tax year rather than overwrite
                        // the same one.
                        const cy = new Date().getFullYear();
                        const seedYear = Number(w.tax_year) || cy - 1;
                        const suggested = seedYear >= cy ? cy : seedYear + 1;
                        setW2SeedFromSaved(w);
                        setW2Years([]);
                        setW2YearIndex(0);
                        setTaxFormYear(suggested);
                        setW2PromptOpen(false);
                        setW2ModalOpen(true);
                      }}
                      className="px-2.5 py-1 rounded-md bg-zinc-900 border border-emerald-600/40 hover:border-emerald-400 hover:bg-emerald-900/30 text-emerald-200 text-xs font-semibold"
                      data-testid={`w2-reuse-${w.id}`}
                      title={`Reuse ${w.tax_year} numbers for a brand-new year (editable)`}
                    >
                      Add another year (from {w.tax_year})
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end">
              <button
                onClick={() => setW2PromptOpen(false)}
                className="px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-200 text-sm font-medium"
                data-testid="w2-prompt-no"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}

      <W2CreatorModal
        open={w2ModalOpen}
        onClose={() => {
          // If the user closes early (Cancel), abort the whole queue.
          setW2ModalOpen(false);
          setW2Years([]);
          setW2YearIndex(0);
          setW2SeedFromSaved(null);
        }}
        onFinished={() => {
          // Called by W2CreatorModal AFTER a successful download.  If
          // more years remain in the queue, hop to the next one; else
          // close everything.
          // 2026-02-08 — also refresh saved W-2s so the newly-persisted
          // one shows up in the client's "add another year" list.
          refreshSavedW2sForClient(currentClientId);
          refreshAllSavedW2Counts();
          const nextIdx = w2YearIndex + 1;
          if (nextIdx < w2Years.length) {
            setW2YearIndex(nextIdx);
            setTaxFormYear(w2Years[nextIdx]);
            // Toggle open state so the modal remounts and picks up the
            // new initialYear (its internal state seeds from that prop).
            setW2ModalOpen(false);
            setTimeout(() => setW2ModalOpen(true), 50);
          } else {
            setW2ModalOpen(false);
            setW2Years([]);
            setW2YearIndex(0);
            setW2SeedFromSaved(null);
            toast.success(`${w2Years.length || 1} W-2${w2Years.length > 1 ? 's' : ''} downloaded ✓`);
          }
        }}
        // Progress indicator (rendered in the modal header) — falsy when
        // only one year is being generated so single-year users don't
        // see "1 of 1".
        queueInfo={w2Years.length > 1 ? { index: w2YearIndex, total: w2Years.length } : null}
        formData={formData}
        initialYear={taxFormYear}
        paystubClientId={currentClientId}
        initialSavedW2={w2SeedFromSaved}
      />
      <NEC1099CreatorModal
        open={necModalOpen}
        onClose={() => setNecModalOpen(false)}
        formData={formData}
        initialYear={taxFormYear}
      />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// IntakeLinkModal — admin generates a shareable link, sees pending
// customer submissions, and one-click imports them as new clients.
// ═══════════════════════════════════════════════════════════════════
function IntakeLinkModal({ onClose, onImported }) {
  const [pending, setPending] = React.useState([]);
  const [loadingPending, setLoadingPending] = React.useState(false);
  const [importingId, setImportingId] = React.useState(null);

  const fetchPending = React.useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await axios.get(`${API}/api/paystub-intake/pending`);
      setPending((res.data || []).filter((p) => p.status === 'submitted'));
    } catch (e) { /* silent */ }
    finally { setLoadingPending(false); }
  }, []);

  React.useEffect(() => { fetchPending(); }, [fetchPending]);

  const importPending = async (intakeId) => {
    setImportingId(intakeId);
    try {
      const res = await axios.post(`${API}/api/paystub-intake/${intakeId}/import`, {});
      toast.success('Imported — the new client is in your list');
      await fetchPending();
      if (onImported) onImported(res.data.client_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImportingId(null); }
  };

  const dismissPending = async (intakeId) => {
    if (!window.confirm('Dismiss this intake? You can not undo this.')) return;
    try { await axios.delete(`${API}/api/paystub-intake/${intakeId}`); fetchPending(); }
    catch { toast.error('Failed to dismiss'); }
  };

  const permanentUrl = `${window.location.origin}/paystub-intake`;
  const copyPermanent = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(permanentUrl); toast.success('Permanent link copied');
    } else {
      const t = document.createElement('textarea'); t.value = permanentUrl;
      document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t);
      toast.success('Permanent link copied');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col overflow-y-auto"
      data-testid="intake-modal">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-zinc-900/95 border-b border-zinc-700 sticky top-0 z-10">
        <button onClick={onClose}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold"
          data-testid="intake-modal-close">← Back</button>
        <span className="text-sm font-bold text-fuchsia-200">🔗 Client Intake Link</span>
        <span className="w-16" />
      </div>

      <div className="max-w-3xl w-full mx-auto p-4 space-y-4">
        {/* ─── Permanent reusable link ────────────────────────── */}
        <div className="rounded-xl border-2 border-cyan-500/50 bg-gradient-to-br from-cyan-500/15 to-blue-500/5 p-4"
          data-testid="intake-generic-panel">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">📌</span>
            <h3 className="text-sm font-bold text-white">Your Intake Link (permanent, never changes)</h3>
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/30 border border-cyan-500/50 text-cyan-100 uppercase tracking-wider font-bold">
              Reusable
            </span>
          </div>
          <p className="text-[11px] text-zinc-300 mb-3">
            One link for every client. Save it to your iPhone <b className="text-cyan-200">Text Replacement</b> once, then text or email it to anyone. Every submission arrives in your inbox and shows up below for one-click import.
          </p>
          <div className="flex gap-2 flex-wrap items-center">
            <input readOnly value={permanentUrl}
              className="flex-1 min-w-[280px] px-3 py-2 bg-zinc-950 border border-cyan-500/40 rounded-lg text-cyan-100 text-sm font-mono"
              onClick={(e) => e.target.select()}
              data-testid="intake-generic-url" />
            <Button onClick={copyPermanent}
              className="bg-cyan-500 hover:bg-cyan-400 text-white font-bold"
              data-testid="intake-generic-copy">Copy</Button>
            <a href={`sms:?body=${encodeURIComponent('Please fill out my paystub intake form: ' + permanentUrl)}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-bold"
              data-testid="intake-generic-text">Text</a>
            <a href={`mailto:?subject=${encodeURIComponent('Paystub Intake Form')}&body=${encodeURIComponent('Please fill out this form so I can prepare your paystub: ' + permanentUrl)}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold"
              data-testid="intake-generic-email">Email</a>
          </div>
        </div>

        <div className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-4">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="text-sm font-bold text-white">
              Client submissions ready to import
              {pending.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px]">
                  {pending.length} new
                </span>
              )}
            </h3>
            <Button variant="outline" size="sm" onClick={fetchPending} disabled={loadingPending}
              className="text-xs border-zinc-600 text-zinc-300">
              {loadingPending ? '…' : 'Refresh'}
            </Button>
          </div>
          {pending.length === 0 && (
            <p className="text-xs text-zinc-500 italic">No submissions yet. When a client fills out your link, they'll appear here.</p>
          )}
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.id}
                className="rounded-lg border border-zinc-700 bg-zinc-950/60 p-3"
                data-testid={`intake-pending-${p.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-white">{p.submission?.fullName || '—'}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      {p.submission?.noJobInfo ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] font-bold uppercase tracking-wider">
                          No Employer Info
                        </span>
                      ) : (
                        <span className="text-fuchsia-300">{p.submission?.jobName || '—'}</span>
                      )}
                      {' · '}
                      ${Number(p.submission?.grossPerCheck || 0).toLocaleString(undefined, {minimumFractionDigits: 2})} {p.submission?.amountType === 'net' ? '(net)' : '(gross)'} / {p.submission?.payFrequency || '—'}
                      {' · '}
                      <span className="text-cyan-300">{p.submission?.numStubs || 1} stub{(p.submission?.numStubs || 1) !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      Submitted {p.submitted_at ? new Date(p.submitted_at).toLocaleString() : '—'}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" onClick={() => importPending(p.id)}
                      disabled={importingId === p.id}
                      className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-bold"
                      data-testid={`intake-import-${p.id}`}>
                      {importingId === p.id ? 'Importing…' : 'Import as Client'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => dismissPending(p.id)}
                      className="border-red-500/40 text-red-300 hover:bg-red-500/20 text-xs">✕</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// IntakeReferenceSidebar — floating top-right panel that keeps every
// field the client submitted via the intake form always visible so the
// admin doesn't have to re-hunt for details across wizard steps.
// Only renders when a client was actually imported from an intake
// (form_data.sourceIntakeId is set).  Any field label / value can be
// tapped to copy that value straight to the clipboard.  Feb 2026.
// ═══════════════════════════════════════════════════════════════════
function IntakeReferenceSidebar({ formData }) {
  const [open, setOpen] = React.useState(true);
  const [copiedKey, setCopiedKey] = React.useState(null);

  if (!formData || !formData.sourceIntakeId) return null;

  const copy = (key, val) => {
    if (!val) return;
    const txt = String(val);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(txt);
    } else {
      const ta = document.createElement('textarea');
      ta.value = txt;
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1200);
  };

  const Row = ({ k, label, val }) => {
    if (!val) return null;
    return (
      <button
        type="button"
        onClick={() => copy(k, val)}
        className="w-full text-left px-2 py-1 -mx-1 rounded hover:bg-amber-500/10 transition group"
        data-testid={`intake-ref-${k}`}
        title="Click to copy"
      >
        <div className="text-[10px] uppercase tracking-wider text-amber-300/70 leading-none">{label}</div>
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-white font-medium break-all">{val}</span>
          {copiedKey === k ? (
            <span className="text-[10px] text-emerald-300 font-bold">✓ copied</span>
          ) : (
            <Copy className="w-2.5 h-2.5 text-zinc-500 opacity-0 group-hover:opacity-100 transition" />
          )}
        </div>
      </button>
    );
  };

  const employeeAddr = [formData.employeeAddress, formData.employeeCity, formData.employeeState, formData.employeeZip].filter(Boolean).join(', ');
  const companyAddr = [formData.companyAddress1, formData.companyCity, formData.companyState, formData.companyZip].filter(Boolean).join(', ');
  const stubs = Array.isArray(formData.paystubs) ? formData.paystubs : [];
  const payDates = stubs.map((s) => s.payDate).filter(Boolean).join(', ');
  const grossAmt = stubs[0]?.current?.grossPay;
  const netAmt = stubs[0]?.current?.netPay;

  return (
    <div
      className={`fixed top-24 right-3 z-40 transition-all ${open ? 'w-72' : 'w-11'}`}
      data-testid="intake-ref-sidebar"
    >
      <div className="rounded-xl border border-amber-500/40 bg-zinc-950/95 backdrop-blur shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-2.5 py-2 bg-gradient-to-r from-amber-600/30 to-orange-600/20 hover:from-amber-600/40 border-b border-amber-500/30 transition"
          data-testid="intake-ref-toggle"
          aria-label={open ? 'Collapse intake reference' : 'Expand intake reference'}
        >
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-200 uppercase tracking-wider">
            <FileText className="w-3.5 h-3.5" />
            {open && 'Client Intake'}
          </span>
          {open && <X className="w-3.5 h-3.5 text-amber-300" />}
        </button>

        {open && (
          <div className="p-3 space-y-2.5 max-h-[calc(100vh-140px)] overflow-y-auto text-white">
            <p className="text-[10px] text-zinc-500 leading-tight italic">
              Tap any field to copy. Everything the client submitted.
            </p>

            <section className="space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-300 border-b border-cyan-500/20 pb-0.5">
                Employee
              </div>
              <Row k="name" label="Full Name" val={formData.employeeName} />
              <Row k="addr" label="Address" val={employeeAddr} />
              <Row k="ssn" label="SSN (last 4)" val={formData.ssnLastFour} />
              <Row k="email" label="Email" val={formData.employeeEmail} />
              <Row k="phone" label="Phone" val={formData.employeePhone} />
            </section>

            <section className="space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-orange-300 border-b border-orange-500/20 pb-0.5">
                Employer
              </div>
              <Row k="co-name" label="Company" val={formData.companyName || '(not provided)'} />
              <Row k="co-addr" label="Address" val={companyAddr || '(not provided)'} />
            </section>

            <section className="space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 border-b border-emerald-500/20 pb-0.5">
                Pay
              </div>
              <Row
                k="amt"
                label={`Per-Check (${(formData.amountType || 'gross').toUpperCase()})`}
                val={grossAmt ? `$${Number(grossAmt).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}
              />
              {netAmt ? (
                <Row k="net" label="Net (est.)" val={`$${Number(netAmt).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
              ) : null}
              <Row k="freq" label="Frequency" val={formData.payFrequency} />
              <Row k="stubs" label="Stubs Requested" val={formData.numStubs ? String(formData.numStubs) : ''} />
              <Row k="dates" label="Pay Dates" val={payDates} />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// IntakeFrontPanel — mini banner on the Payday ST landing page that:
//   • Shows pending intake submissions (with Import buttons)
//   • Lets the admin generate + copy/email a fresh intake link in-place
// ═══════════════════════════════════════════════════════════════════
function IntakeFrontPanel({ onOpenModal, onImported }) {
  const [pending, setPending] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [importingId, setImportingId] = React.useState(null);
  // Feb 2026 — pin the intake URL to PRODUCTION (matches the owner's
  // iPhone Text Replacement shortcut) and expose an editable QR field
  // right underneath the link banner.  Punch any URL in → QR renders
  // live below.  Clients scan with camera → opens in default browser,
  // sidestepping the Android "Could not find an appropriate application"
  // handoff error some phones throw for tapped links in RCS.
  const PORTAL_ORIGIN = 'https://pdf-gen-preview-1.emergent.host';
  const intakeUrl = `${PORTAL_ORIGIN}/paystub-intake`;
  const [qrUrl, setQrUrl] = React.useState(intakeUrl);
  const qrRef = React.useRef(null);

  const fetchPending = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/paystub-intake/pending`);
      setPending((res.data || []).filter((p) => p.status === 'submitted'));
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchPending(); }, [fetchPending]);

  const importOne = async (intakeId) => {
    setImportingId(intakeId);
    try {
      const res = await axios.post(`${API}/api/paystub-intake/${intakeId}/import`, {});
      await fetchPending();
      if (onImported) onImported(res.data.client_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImportingId(null); }
  };

  return (
    <div className="rounded-xl border-2 border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/5 p-4 space-y-3"
      data-testid="intake-front-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-lg">🔗</span>
          <span className="text-sm font-bold text-white">Client Intake Link</span>
          {pending.length > 0 && (
            <span className="ml-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold animate-pulse"
              data-testid="intake-pending-badge">
              {pending.length} NEW SUBMISSION{pending.length !== 1 ? 'S' : ''}
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={onOpenModal}
          className="text-xs border-fuchsia-500/50 text-fuchsia-200 hover:bg-fuchsia-500/20"
          data-testid="intake-front-manage">
          Manage
        </Button>
      </div>

      {/* Pending submissions — top-priority CTA */}
      {pending.length > 0 && (
        <div className="space-y-2" data-testid="intake-front-pending-list">
          {pending.map((p) => (
            <div key={p.id}
              className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-white">
                    📥 {p.submission?.fullName || '—'}
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-2 py-0.5">
                      Ready to Import
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-300 mt-0.5">
                    {p.submission?.noJobInfo ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] font-bold uppercase tracking-wider">
                        No Employer Info
                      </span>
                    ) : (
                      <>{p.submission?.jobName || '—'}</>
                    )}
                    {' · '}${Number(p.submission?.grossPerCheck || 0).toLocaleString(undefined, {minimumFractionDigits: 2})} {p.submission?.amountType === 'net' ? '(net)' : '(gross)'} / {p.submission?.payFrequency || '—'}
                    {' · '}<span className="text-cyan-300">{p.submission?.numStubs || 1} stub{(p.submission?.numStubs || 1) !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    Submitted {p.submitted_at ? new Date(p.submitted_at).toLocaleString() : '—'}
                  </div>
                </div>
                <Button size="sm" onClick={() => importOne(p.id)} disabled={importingId === p.id}
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white text-xs font-bold shadow-lg shadow-emerald-500/30"
                  data-testid={`intake-front-import-${p.id}`}>
                  {importingId === p.id ? 'Importing…' : 'Import & Review →'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Permanent generic link — always visible, never changes */}
      <div className="pt-2 border-t border-zinc-800/60">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-300">📌 Permanent Link (save to iPhone text-replacement)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input readOnly value={intakeUrl}
            onClick={(e) => e.target.select()}
            className="flex-1 min-w-[220px] px-2.5 py-1.5 bg-zinc-950 border border-cyan-500/40 rounded-md text-cyan-100 text-[11px] font-mono"
            data-testid="intake-front-generic-link" />
          <Button size="sm" onClick={() => {
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(intakeUrl); toast.success('Permanent link copied');
              } else {
                const t = document.createElement('textarea'); t.value = intakeUrl;
                document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t);
                toast.success('Permanent link copied');
              }
            }}
            className="h-8 px-2.5 bg-cyan-500 hover:bg-cyan-400 text-white text-xs font-bold"
            data-testid="intake-front-generic-copy">Copy</Button>
          <a href={`sms:?body=${encodeURIComponent('Please fill out my paystub intake form: ' + intakeUrl)}`}
            className="inline-flex items-center px-2.5 h-8 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-xs font-bold"
            data-testid="intake-front-generic-text">Text</a>
          <a href={`mailto:?subject=${encodeURIComponent('Paystub Intake Form')}&body=${encodeURIComponent('Please fill out this form so I can prepare your paystub: ' + intakeUrl)}`}
            className="inline-flex items-center px-2.5 h-8 rounded-md bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold"
            data-testid="intake-front-generic-email">Email</a>
        </div>

        {/* Always-visible QR panel — URL on top, QR under it.  Punch in
            any link and the QR updates live.  Feb 2026 — this fixes the
            "Could not find an appropriate application" Android error some
            clients hit when tapping raw URLs in RCS / Google Messages.  */}
        <div className="mt-3 rounded-lg border border-amber-500/40 bg-black/40 p-3 space-y-2"
          data-testid="intake-front-qr-panel">
          <label className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold block inline-flex items-center gap-1">
            <QrCode className="w-3 h-3" /> URL &amp; QR (punch in any link — QR renders under it)
          </label>
          <input
            type="text"
            value={qrUrl}
            onChange={(e) => setQrUrl(e.target.value)}
            placeholder="https://…"
            className="w-full px-2.5 py-1.5 bg-zinc-950 border border-amber-500/40 rounded-md text-amber-100 text-[11px] font-mono focus:outline-none focus:border-amber-400"
            data-testid="intake-front-qr-input"
            spellCheck={false}
          />
          <div className="flex flex-col sm:flex-row items-start gap-3">
            <div className="bg-white p-3 rounded-lg" data-testid="intake-front-qr-canvas">
              {qrUrl.trim() ? (
                <QRCodeCanvas ref={qrRef} value={qrUrl.trim()} size={192} level="H" includeMargin={false} />
              ) : (
                <div className="text-slate-500 text-xs italic w-[192px] h-[192px] flex items-center justify-center text-center">Paste a URL above</div>
              )}
            </div>
            <div className="flex flex-col gap-1.5 flex-1">
              <p className="text-[11px] text-slate-300 leading-snug">
                Screenshot this QR and text/email it to any client. Their camera opens the link straight in their browser — bypasses the Android app-link error.
              </p>
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" onClick={() => setQrUrl(intakeUrl)}
                  className="h-7 px-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] font-medium"
                  data-testid="intake-front-qr-reset">
                  Reset to intake link
                </Button>
                <Button size="sm" onClick={() => {
                    const canvas = qrRef.current;
                    if (!canvas) return;
                    const url = canvas.toDataURL('image/png');
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'paystub-intake-qr.png';
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  }}
                  disabled={!qrUrl.trim()}
                  className="h-7 px-2 bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40"
                  data-testid="intake-front-qr-download">
                  <Download className="w-3 h-3" /> Download PNG
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

